import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sorgenteDati } from '@/api/client';
import { getMenuPubblico } from '@/features/redazione/apiDashboard';
import { MAIN_NAV } from './navigation';
import type { NavItem } from './navigation';

/**
 * Le voci del megamenu, dalla configurazione della redazione (decisione 82).
 * ---------------------------------------------------------------------------
 * `GET /api/v2/menu/?ambito=principale` serve solo ciò che è **pubblicato e
 * visibile**, con l'`url` già risolto: il frontend non deve conoscere la
 * mappa delle rotte, e per le pagine redazionali riceve `/p/{slug}/`
 * (decisione 71).
 *
 * **Elenco vuoto = nessuna configurazione, e si serve `MAIN_NAV`.** Non è un
 * ripiego d'emergenza: è il comportamento dichiarato dal contratto. Finché
 * nessuno ha configurato un menu, il sito ha la navigazione predefinita —
 * che è meglio di un sito senza navigazione, e infinitamente meglio di una
 * navigazione a metà.
 *
 * Lo stesso vale se la richiesta fallisce: la navigazione non è il posto
 * dove mostrare un errore di rete.
 *
 * In modalità `fixtures` la chiamata non parte affatto: i dataset statici non
 * contengono configurazioni di menu, e un 404 a ogni pagina in sviluppo
 * sarebbe rumore che nasconde gli errori veri.
 */
export function useMenuConfigurato(comune?: string): {
  readonly voci: readonly NavItem[];
  /** `true` = le voci vengono dalla redazione, non dal codice. */
  readonly configurato: boolean;
} {
  const { i18n } = useTranslation();
  const lingua = i18n.language || 'it';
  const [voci, setVoci] = useState<readonly NavItem[] | null>(null);

  useEffect(() => {
    if (sorgenteDati() !== 'api') return;
    const controller = new AbortController();
    getMenuPubblico(
      { ambito: 'principale', lang: lingua, ...(comune ? { comune } : {}) },
      controller.signal,
    )
      .then((risposta) => {
        if (controller.signal.aborted) return;
        const configurate = risposta.risultati
          .filter((voce) => voce.url)
          .map<NavItem>((voce) => ({
            to: voce.url,
            // Le etichette configurate sono già nella lingua richiesta (con
            // fallback dichiarato sull'italiano): non passano da `t()`, che
            // le cercherebbe in un dizionario dove non stanno.
            labelKey: `menu_configurato_${voce.id}`,
            fallback: voce.etichetta,
          }));
        setVoci(configurate.length > 0 ? configurate : null);
      })
      .catch(() => {
        if (!controller.signal.aborted) setVoci(null);
      });
    return () => controller.abort();
  }, [lingua, comune]);

  return voci ? { voci, configurato: true } : { voci: MAIN_NAV, configurato: false };
}
